import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/App';
import axios from 'axios';
import Navigation from '@/components/Navigation';
import StarRating from '@/components/StarRating';
import {
  MessageSquare,
  Send,
  Clock,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Quote
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_META = {
  pending: {
    label: 'Pending moderation',
    icon: Clock,
    badge: 'bg-amber-100 text-amber-900',
    description: 'All Build Consult is reviewing your feedback before it appears on the public site.'
  },
  approved: {
    label: 'Approved & published',
    icon: CheckCircle2,
    badge: 'bg-green-100 text-green-800',
    description: 'Your feedback is live on the All Build Consult company page. Thank you!'
  },
  rejected: {
    label: 'Not published',
    icon: XCircle,
    badge: 'bg-rose-100 text-rose-800',
    description: 'This feedback was not published to the public site.'
  }
};

export default function Feedback() {
  const { token, user } = useAuth();

  const [stars, setStars] = useState(0);
  const [title, setTitle] = useState('');
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [mine, setMine] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchMine = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/reviews/mine`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMine(res.data);
    } catch (err) {
      console.error('Failed to load your reviews', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchMine();
  }, [fetchMine]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (!stars || !title.trim() || !comment.trim()) {
      setError('Please give a star rating, a short title, and your feedback.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/reviews`,
        { stars, title: title.trim(), comment: comment.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccess('Thanks — your feedback has been submitted for review by All Build Consult.');
      setStars(0);
      setTitle('');
      setComment('');
      fetchMine();
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not submit your feedback. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="feedback-page" className="min-h-screen bg-slate-50">
      <Navigation />
      <div className="max-w-5xl mx-auto px-6 py-8 mt-16">
        <div className="mb-8">
          <h1
            className="text-4xl font-bold tracking-tight text-black mb-2"
            style={{ fontFamily: 'Outfit' }}
          >
            Client Reviews &amp; Feedback
          </h1>
          <p className="text-slate-600">
            Share your experience with All Build Consult. Approved feedback may be featured on the
            public company page.
          </p>
        </div>

        {/* Submit form */}
        <div className="grid-border bg-white p-6 mb-8">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 bg-blue-50 rounded-sm flex items-center justify-center">
              <MessageSquare className="w-5 h-5 text-blue-700" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-black" style={{ fontFamily: 'Outfit' }}>
                Leave a review
              </h2>
              <p className="text-xs text-slate-500">
                Posting as <strong>{user?.name || 'You'}</strong>. Submissions are moderated by All
                Build Consult before going live.
              </p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-600 mb-2">
                Overall rating
              </label>
              <StarRating
                value={stars}
                size="lg"
                interactive
                onChange={setStars}
                testIdPrefix="feedback-star"
              />
            </div>
            <div>
              <label
                htmlFor="feedback-title"
                className="block text-xs font-semibold uppercase tracking-wide text-slate-600 mb-2"
              >
                Title
              </label>
              <input
                id="feedback-title"
                data-testid="feedback-title-input"
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                maxLength={120}
                placeholder="e.g. Saved us thousands on a tricky reno"
                className="w-full px-3 py-2 border border-slate-300 rounded-sm focus:outline-none focus:border-black"
              />
            </div>
            <div>
              <label
                htmlFor="feedback-comment"
                className="block text-xs font-semibold uppercase tracking-wide text-slate-600 mb-2"
              >
                Your feedback
              </label>
              <textarea
                id="feedback-comment"
                data-testid="feedback-comment-input"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                maxLength={2000}
                rows={5}
                placeholder="Tell us what worked, what we could improve, and how we helped you on your project."
                className="w-full px-3 py-2 border border-slate-300 rounded-sm focus:outline-none focus:border-black resize-none"
              />
              <p className="text-[11px] text-slate-400 mt-1">{comment.length}/2000</p>
            </div>

            {error && (
              <div className="flex items-start gap-2 p-3 bg-rose-50 border border-rose-200 rounded-sm text-sm text-rose-800">
                <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                {error}
              </div>
            )}
            {success && (
              <div className="flex items-start gap-2 p-3 bg-green-50 border border-green-200 rounded-sm text-sm text-green-800">
                <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
                {success}
              </div>
            )}

            <button
              data-testid="feedback-submit-button"
              type="submit"
              disabled={submitting}
              className="px-5 py-3 bg-black text-white rounded-sm hover:bg-slate-800 transition-all text-sm font-medium inline-flex items-center gap-2 disabled:opacity-50"
            >
              <Send className="w-4 h-4" />
              {submitting ? 'Submitting…' : 'Submit for review'}
            </button>
          </form>
        </div>

        {/* My past reviews */}
        <div className="grid-border bg-white p-6">
          <h2 className="text-lg font-semibold text-black mb-4" style={{ fontFamily: 'Outfit' }}>
            Your submissions
          </h2>
          {loading ? (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-black"></div>
            </div>
          ) : mine.length === 0 ? (
            <p data-testid="feedback-empty-state" className="text-sm text-slate-500">
              You haven't submitted any feedback yet.
            </p>
          ) : (
            <ul className="space-y-4">
              {mine.map((r) => {
                const meta = STATUS_META[r.status] || STATUS_META.pending;
                const Icon = meta.icon;
                return (
                  <li
                    key={r.id}
                    data-testid="feedback-history-item"
                    className="grid-border bg-slate-50 p-4"
                  >
                    <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
                      <div className="flex items-center gap-3">
                        <StarRating value={r.stars} size="sm" />
                        <h3 className="font-semibold text-black">{r.title}</h3>
                      </div>
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[11px] font-semibold uppercase tracking-wide ${meta.badge}`}
                      >
                        <Icon className="w-3 h-3" />
                        {meta.label}
                      </span>
                    </div>
                    <p className="text-sm text-slate-700 mb-2 whitespace-pre-line">{r.comment}</p>
                    <p className="text-[11px] text-slate-500">
                      Submitted {new Date(r.created_at).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}
                      {' · '}
                      {meta.description}
                    </p>
                    {r.moderator_note && (
                      <p className="text-[11px] text-slate-600 mt-2 italic flex items-start gap-1">
                        <Quote className="w-3 h-3 mt-0.5" />
                        Moderator note: {r.moderator_note}
                      </p>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
